import React, { useEffect, useState } from "react";
import {
  Activity, Loader2, CheckCircle2, AlertTriangle, XCircle, ShieldAlert, RefreshCw,
} from "lucide-react";
import { Head } from "./screens/shared";
import { sr } from "../../lib/sr";

const STATUS = {
  ok: { color: "#067647", bg: "#DCFCE7", Icon: CheckCircle2, label: "Healthy" },
  warn: { color: "#B45309", bg: "#FEF3C7", Icon: AlertTriangle, label: "Attention" },
  error: { color: "#B91C1C", bg: "#FEE2E2", Icon: XCircle, label: "Action needed" },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.warn;
  return (
    <span className="sr-badge" style={{ background: s.bg, color: s.color }} data-testid={`health-status-${status}`}>
      <s.Icon size={12} style={{ verticalAlign: "-2px" }} /> {s.label}
    </span>
  );
}

export default function TrackingHealth() {
  const [data, setData] = useState(null);
  const [quarantine, setQuarantine] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([sr.trackingHealth(), sr.trackingQuarantine()])
      .then(([h, q]) => { setData(h); setQuarantine(q); })
      .catch(() => setData({ checks: [], summary: {}, overall: "warn" }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const overall = STATUS[data?.overall] || STATUS.warn;

  return (
    <div data-testid="tracking-health">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <Head title="Tracking Health" sub="Live status of Meta connections, webhooks, lead routing and attribution." />
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={load} data-testid="health-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="sr-card" style={{ display: "flex", alignItems: "center", gap: 12, background: overall.bg, border: `1px solid ${overall.color}33`, marginBottom: 16 }}>
        <Activity size={22} color={overall.color} />
        <div style={{ flex: 1 }}>
          <strong style={{ color: "var(--navy)" }}>Overall: {overall.label}</strong>
          <div className="sr-muted" style={{ fontSize: 13 }}>
            {data.summary.ok || 0} healthy · {data.summary.warn || 0} need attention · {data.summary.error || 0} action needed
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14 }}>
        {data.checks.map((c) => (
          <div key={c.key} className="sr-card" data-testid={`health-check-${c.key}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
              <strong style={{ color: "var(--navy)" }}>{c.label}</strong>
              <StatusPill status={c.status} />
            </div>
            <p style={{ fontSize: 13, color: "var(--navy)", margin: "0 0 6px" }}>{c.detail}</p>
            {c.recommendation && (
              <p className="sr-muted" style={{ fontSize: 12, margin: 0 }}>💡 {c.recommendation}</p>
            )}
          </div>
        ))}
      </div>

      <div className="sr-card" style={{ marginTop: 16, padding: 0, overflow: "hidden" }} data-testid="health-quarantine">
        <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--border)" }}>
          <ShieldAlert size={16} color="#B45309" />
          <strong style={{ color: "var(--navy)" }}>Unrouted inbound (quarantined)</strong>
          <span className="sr-badge sr-badge-gray" style={{ marginLeft: "auto" }}>{quarantine?.total ?? 0}</span>
        </div>
        {!quarantine || quarantine.items.length === 0 ? (
          <div className="sr-empty" style={{ padding: 20 }}>No unmatched inbound — every call, text and email is routing to a customer.</div>
        ) : (
          <div>
            {quarantine.items.slice(0, 25).map((q) => (
              <div key={q.id} style={{ display: "flex", gap: 10, padding: "10px 14px", borderTop: "1px solid var(--border)", fontSize: 13, alignItems: "center" }}>
                <span className="sr-badge sr-badge-gray">{q.channel}</span>
                <span style={{ color: "var(--navy)" }}>{q.to || q.recipient || "—"}</span>
                <span className="sr-muted" style={{ marginLeft: "auto", fontSize: 12 }}>{(q.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
        <div className="sr-muted" style={{ fontSize: 12, padding: "10px 14px" }}>
          Unmatched inbound is safely quarantined and never routed to the wrong customer. Bind these numbers/emails in Lead settings to route them.
        </div>
      </div>
    </div>
  );
}
